import React from "react";

/**
 * StatBar — Visual side-by-side comparison bar for a single match statistic.
 * Blue bar (left) represents the home team, red bar (right) represents the away team.
 */
function StatBar({ label, home, away }) {
  const homeVal = parseFloat(home) || 0;
  const awayVal = parseFloat(away) || 0;
  const total = homeVal + awayVal;
  const homePct = total > 0 ? (homeVal / total) * 100 : 50;

  return (
    <div className="py-3">
      <div className="flex justify-between text-sm mb-1.5">
        <span className="text-white font-semibold">{home ?? "-"}</span>
        <span className="text-gray-400 text-xs uppercase tracking-wide">
          {label}
        </span>
        <span className="text-white font-semibold">{away ?? "-"}</span>
      </div>
      <div className="flex h-1.5 rounded-full overflow-hidden bg-white/10 gap-0.5">
        <div
          className="bg-blue-500 rounded-l-full transition-all"
          style={{ width: `${homePct}%` }}
        />
        <div
          className="bg-red-500 rounded-r-full transition-all"
          style={{ width: `${100 - homePct}%` }}
        />
      </div>
    </div>
  );
}

const extractStats = (event) => {
  const raw = event?.matchStats || event?.statistics || event?.stats;

  if (!Array.isArray(raw) || raw.length === 0) return null;

  return raw
    .map((s) => ({
      label: s.label || s.name || s.type,
      home: s.home ?? s.homeValue,
      away: s.away ?? s.awayValue,
    }))
    .filter((s) => s.label != null);
};

const MatchStats = ({ event }) => {
  const homeTeam = event?.strHomeTeam || event?.homeTeam || "Home";
  const awayTeam = event?.strAwayTeam || event?.awayTeam || "Away";
  const homeTeamLogo = event?.strHomeTeamBadge || event?.homeTeamLogo;
  const awayTeamLogo = event?.strAwayTeamBadge || event?.awayTeamLogo;

  const stats = extractStats(event);

  if (!stats || stats.length === 0) {
    return (
      <div className="px-6 py-12 text-center">
        <div className="text-gray-400 text-lg">
          Match stats are not available yet.
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          {homeTeamLogo && (
            <img
              alt={homeTeam}
              className="w-6 h-6 object-contain"
              src={homeTeamLogo}
            />
          )}
          <span className="text-white font-semibold text-sm sm:text-base uppercase tracking-wide">
            {homeTeam}
          </span>
        </div>

        <span className="text-gray-400 text-xs uppercase tracking-widest">
          Match Stats
        </span>

        <div className="flex items-center gap-2">
          <span className="text-white font-semibold text-sm sm:text-base uppercase tracking-wide">
            {awayTeam}
          </span>
          {awayTeamLogo && (
            <img
              alt={awayTeam}
              className="w-6 h-6 object-contain"
              src={awayTeamLogo}
            />
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto divide-y divide-white/5">
        {stats.map((stat) => (
          <StatBar
            away={stat.away}
            home={stat.home}
            key={stat.label}
            label={stat.label}
          />
        ))}
      </div>
    </div>
  );
};

export default MatchStats;
